import type { SiteVersion } from '../../../../../../core/rendering/types.js';
import type { DeviceType, InsightData } from './insight.js';
import type {
  EnrichWithInsightParams,
  UnstructuredResponse,
  StructuredResponse,
} from '../../types.js';

import { insight } from './insight.js';

let currentUrl: string;
let deferred: {
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
} | null;
let insightPromise: Promise<unknown> | null;

function getPromise(url: string) {
  if (url !== currentUrl) {
    currentUrl = url;
    if (deferred) {
      deferred.reject(new Error('Pending promise rejected'));
    }
    insightPromise = new Promise((resolve, reject) => {
      deferred = {
        resolve,
        reject,
      };
    });
  }

  return { resolve: deferred?.resolve, promise: insightPromise };
}

function setInsightData(url: string, data: InsightData) {
  const { resolve } = getPromise(url);
  if (resolve) {
    resolve(data);
  }
}

const getParamFromTags = (name: string, items?: string[]) => {
  if (!items) return undefined;

  const paramRaw = items.find((item) => item.includes(name));

  return paramRaw ? paramRaw.split('"')[1] : undefined;
};

export const structureResponse = (
  unstructured: UnstructuredResponse,
): StructuredResponse => {
  const { html, css } = unstructured.data.poster;
  const htmlItems = html?.split(' ');
  const campaign = getParamFromTags('data-campaign=', htmlItems);
  const content = getParamFromTags('data-content=', htmlItems);
  const medium = getParamFromTags('data-medium=', htmlItems);

  return {
    html: html || '',
    css,
    campaign: campaign || '',
    medium: medium || '',
    content: content || '',
  };
};

export const enrichWithInsight = (
  structuredResponse: StructuredResponse,
  {
    canonicalUrl = '',
    endpoint = null,
    statisticsId,
    newsroom,
    contentProvider,
    ...rest
  }: EnrichWithInsightParams,
): EnrichedWithInsight => {
  const { html, css } = structuredResponse;
  const docRef = document.referrer;

  const insightData = insight(
    {
      canonicalUrl,
      contentId: statisticsId,
      contentProvider,
      newsroom,
      contentType: 'article',
      ...rest,
    },
    structuredResponse,
    docRef,
  );
  endpoint && setInsightData(endpoint, insightData);

  const encodedCanonicalUrl = encodeURIComponent(canonicalUrl);
  const insightReferrer = `&referer=${insightData.tag}`;
  const updatedHtml = html
    .replace(
      /{{redirect_uri_url_encoded}}/gi,
      encodedCanonicalUrl + insightReferrer,
    )
    .replace(/{{article_id}}/gi, statisticsId);

  return {
    html: updatedHtml,
    css,
    insightData,
  };
};

export const mapSiteversionToDeviceType = (
  siteVersion: SiteVersion,
): DeviceType => (siteVersion === 'phone' ? 'mobile' : siteVersion);

export type EnrichedWithInsight = {
  html?: string;
  css?: string;
  insightData?: InsightReferrer;
};

export type InsightReferrer = {
  source: string;
  content: string;
  campaign: string;
  medium: string;
  tag: string;
};
